<script setup lang="ts">
    import { computed } from 'vue';
    import { useHipSpineStore } from '@/views/3d-planner/hipSpine/hipSpineStore';
    import { useHipSpineXRayModalViewModel } from '@/views/3d-planner/hipSpine/xrayModal/useHipSpineXRayModalViewModel';
    import { useHipSpineXRayEditViewModel } from '@/views/3d-planner/hipSpine/xrayEdit/useHipSpineXRayEditViewModel';
    import HipSpineXRayViewer from '@/views/3d-planner/hipSpine/components/HipSpineXRayViewer.vue';
    import HipSpineXRayImageControls from '@/views/3d-planner/hipSpine/components/HipSpineXRayImageControls.vue';
    import { HIP_SPINE_TARGETS } from '@/domain/analytics/analyticsTargets';

    const store = useHipSpineStore();
    const vm = useHipSpineXRayModalViewModel();
    const editVm = useHipSpineXRayEditViewModel();

    const directionLabel = computed(() => {
        const id = vm.activePostureId;
        if (!id) return 'Anterior: Left';
        return editVm.viewedSide(id) === 'right' ? 'Anterior: Right' : 'Anterior: Left';
    });

    const directionFlipped = computed(() => directionLabel.value === 'Anterior: Left');

    function toggleDirection() {
        if (vm.activePostureId) editVm.toggleSide(vm.activePostureId);
    }
</script>

<template>
    <v-card-item class="pa-3 xray-container">
        <v-tabs
            v-model="vm.activePostureId"
            class="tab-heading"
            color="primary"
            grow
            :data-analytics-id="HIP_SPINE_TARGETS.xRayTabs"
        >
            <v-tab
                v-for="tab in vm.postureTabs"
                :key="tab.value"
                :value="tab.value"
                :disabled="vm.isTabDisabled(tab.value)"
            >
                <span class="pending-label">
                    {{ tab.label }}
                    <v-icon
                        v-if="!vm.isTabDisabled(tab.value) && vm.isPosturePending(tab.value)"
                        icon="mdi-information-outline"
                        color="warning"
                        size="18"
                        class="pending-icon"
                    />
                </span>
            </v-tab>
        </v-tabs>
        <v-divider />
        <v-alert
            v-if="vm.pendingPostureCount > 0"
            type="warning"
            variant="tonal"
            density="compact"
            icon="mdi-alert-circle-outline"
            icon-size="24"
            class="rounded-lg font-weight-bold"
        >
            Action Required: Please review and approve changes on all X-ray tabs ({{
                vm.totalPostureCount - vm.pendingPostureCount
            }}/{{ vm.totalPostureCount }} approved).
        </v-alert>
        <v-alert
            v-else-if="vm.totalPostureCount > 0"
            type="success"
            variant="tonal"
            density="compact"
            icon="mdi-check"
            class="rounded-lg font-weight-bold"
        >
            All X-ray tabs have been approved.
        </v-alert>
        <div
            class="image-panel rounded-lg"
            :class="{ 'image-panel-pending': vm.pendingPostureCount > 0 }"
        >
            <button
                v-if="vm.activePostureId"
                type="button"
                class="direction-toggle"
                data-testid="hip-spine-direction-toggle"
                :disabled="vm.isSaving"
                @click="toggleDirection"
            >
                <span class="direction-toggle-icon">
                    <v-icon
                        icon="mdi-logout "
                        size="24"
                        :class="{ 'mdi-flip-h': directionFlipped }"
                    />
                </span>
                <span class="direction-toggle-label">{{ directionLabel }}</span>
            </button>
            <div class="image-viewport">
                <div v-if="store.isLoading" class="loading">
                    <v-progress-circular indeterminate color="white" size="48" />
                </div>
                <HipSpineXRayViewer v-else />
            </div>
            <HipSpineXRayImageControls :data-analytics-id="HIP_SPINE_TARGETS.xRayImageControls" />
        </div>
    </v-card-item>
</template>

<style scoped lang="scss">
    .xray-container {
        width: calc(900px - 380px);
    }

    .tab-heading * {
        font-size: 13px;
        text-transform: none;
    }

    .pending-label {
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }

    .image-panel {
        width: 495px;
        height: calc(100vh - 334px);
        display: flex;
        flex-direction: column;
        background-color: black;
        position: relative;
        margin: 0 auto;

        &-pending {
            height: calc(100vh - 352px);
        }
    }

    .image-viewport {
        position: relative;
        flex: 1 1 auto;
        min-height: 0;
    }

    .direction-toggle {
        position: absolute;
        top: 12px;
        z-index: 2;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        padding: 0;
        border: none;
        background: transparent;
        cursor: pointer;

        &-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            padding: 20px;
            border-radius: 50%;
            background-color: rgba(var(--v-theme-background));
            color: rgba(var(--v-theme-secondary-text));
        }

        &-label {
            padding: 10px;
            border-radius: 2px;
            font-size: 12px;
            color: rgba(var(--v-theme-background));
            background-color: rgba(#262626, 0.76);
            opacity: 0;
            transition: opacity 0.15s ease;
        }

        &:hover &-label,
        &:focus-visible &-label {
            opacity: 1;
        }
    }

    .loading {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
    }

    .v-alert {
        font-size: 14px;

        :deep(.v-alert__prepend) {
            grid-row: 1 / -1;
            align-self: center;
        }
    }
</style>
